import { AdminReflection } from './admin-reflection'
import { TaskReflection } from './task-reflection'
import { Quiz } from './quiz'
import { TaskTypeEnum } from './task-type-enum'
import { TaskStatusEnum } from './task-status-enum'
import { TaskSetting } from './task-setting'

export interface TaskInterface {
  /**
   * 任务待批阅用户集合
   */
  needReviewedRids?: number[]
  /**
   * 任务关联的管理员集合
   */
  adminReflections?: AdminReflection[]
  /**
   * 前序任务名集合
   *  - 仅活动任务有
   */
  prevTaskNames?: string[]
  /**
   * 活动任务关联的活动参与人员
   * expand=task.task_reflections会返回该值
   */
  taskReflections?: TaskReflection[]
  /**
   * 当前用户任务是否完成
   */
  isCompleted?: boolean
  /**
   * 任务完成时间
   */
  completedAt?: string
  /**
   * 摇号任务-关联的用户数量统计
   */
  waggleReflectionCount?: number
  /**
   * 习题任务总分
   */
  fullScore?: string
  /**
   * 测验数据
   */
  quiz?: Quiz
  /**
   * 测验批阅人数
   */
  reviewNum?: number
  /**
   * 测验提交人数
   */
  submitNum?: number
  /**
   * 任务总人数
   */
  reflectionNum?: number
  /**
   * 主键 ID
   */
  id: number
  /**
   * 学校 ID
   */
  schoolId: number
  /**
   * 创建时间
   */
  createdAt: string
  /**
   * 更新时间
   */
  updatedAt: string
  /**
   * 任务关联活动
   */
  activityId?: number
  /**
   * 名称
   */
  name?: string
  /**
   * 任务类型
   */
  type?: TaskTypeEnum
  /**
   * 任务状态
   */
  status?: TaskStatusEnum
  /**
   * 活动开始时间
   */
  startAt?: string | null
  /**
   * 活动结束时间
   */
  endAt?: string | null
  /**
   * 活动定时开始时间
   */
  timedStartAt?: string | null
  /**
   * 前序任务
   */
  prevTaskIds?: number[]
  /**
   * 是否显示
   */
  isShow?: boolean
  /**
   * 关联的试卷 ID
   */
  paperId?: number
  /**
   * 管理员 ID
   */
  adminIds?: number[]
  /**
   * 其它设置
   */
  setting?: TaskSetting
  /**
   * 描述
   */
  description?: string
  /**
   * 权重
   */
  weight?: number
}

export class Task implements TaskInterface {
  /**
   * 任务待批阅用户集合
   */
  needReviewedRids?: number[]
  /**
   * 任务关联的管理员集合
   */
  adminReflections?: AdminReflection[]
  /**
   * 前序任务名集合
   *  - 仅活动任务有
   */
  prevTaskNames?: string[]
  /**
   * 活动任务关联的活动参与人员
   * expand=task.task_reflections会返回该值
   */
  taskReflections?: TaskReflection[]
  /**
   * 当前用户任务是否完成
   */
  isCompleted?: boolean
  /**
   * 任务完成时间
   */
  completedAt?: string
  /**
   * 摇号任务-关联的用户数量统计
   */
  waggleReflectionCount?: number
  /**
   * 习题任务总分
   */
  fullScore?: string
  /**
   * 测验数据
   */
  quiz?: Quiz
  /**
   * 测验批阅人数
   */
  reviewNum?: number
  /**
   * 测验提交人数
   */
  submitNum?: number
  /**
   * 任务总人数
   */
  reflectionNum?: number
  /**
   * 主键 ID
   */
  id: number
  /**
   * 学校 ID
   */
  schoolId: number
  /**
   * 创建时间
   */
  createdAt: string
  /**
   * 更新时间
   */
  updatedAt: string
  /**
   * 任务关联活动
   */
  activityId?: number
  /**
   * 名称
   */
  name?: string
  /**
   * 任务类型
   */
  type?: TaskTypeEnum
  /**
   * 任务状态
   */
  status?: TaskStatusEnum
  /**
   * 活动开始时间
   */
  startAt?: string | null
  /**
   * 活动结束时间
   */
  endAt?: string | null
  /**
   * 活动定时开始时间
   */
  timedStartAt?: string | null
  /**
   * 前序任务
   */
  prevTaskIds?: number[]
  /**
   * 是否显示
   */
  isShow?: boolean
  /**
   * 关联的试卷 ID
   */
  paperId?: number
  /**
   * 管理员 ID
   */
  adminIds?: number[]
  /**
   * 其它设置
   */
  setting?: TaskSetting
  /**
   * 描述
   */
  description?: string
  /**
   * 权重
   */
  weight?: number

  constructor(props: TaskInterface) {
    const {
      // 重命名变量以规避与 JS 保留词的冲突, 比如 default, with
      needReviewedRids: _needReviewedRids,
      adminReflections: _adminReflections,
      prevTaskNames: _prevTaskNames,
      taskReflections: _taskReflections,
      isCompleted: _isCompleted,
      completedAt: _completedAt,
      waggleReflectionCount: _waggleReflectionCount,
      fullScore: _fullScore,
      quiz: _quiz,
      reviewNum: _reviewNum,
      submitNum: _submitNum,
      reflectionNum: _reflectionNum,
      id: _id,
      schoolId: _schoolId,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      activityId: _activityId,
      name: _name,
      type: _type,
      status: _status,
      startAt: _startAt,
      endAt: _endAt,
      timedStartAt: _timedStartAt,
      prevTaskIds: _prevTaskIds,
      isShow: _isShow,
      paperId: _paperId,
      adminIds: _adminIds,
      setting: _setting,
      description: _description,
      weight: _weight,
      // 可能会有额外字段比如自定义字段不在类型定义中
      ...rest
    } = props

    Object.assign(this, rest)

    this.needReviewedRids = _needReviewedRids
    this.adminReflections = _adminReflections
      ? _adminReflections.map((i) => new AdminReflection(i))
      : _adminReflections
    this.prevTaskNames = _prevTaskNames
    this.taskReflections = _taskReflections
      ? _taskReflections.map((i) => new TaskReflection(i))
      : _taskReflections
    this.isCompleted = _isCompleted
    this.completedAt = _completedAt
    this.waggleReflectionCount = _waggleReflectionCount
    this.fullScore = _fullScore
    this.quiz = _quiz ? new Quiz(_quiz) : _quiz
    this.reviewNum = _reviewNum
    this.submitNum = _submitNum
    this.reflectionNum = _reflectionNum
    this.id = _id
    this.schoolId = _schoolId
    this.createdAt = _createdAt
    this.updatedAt = _updatedAt
    this.activityId = _activityId
    this.name = _name
    this.type = _type
    this.status = _status
    this.startAt = _startAt
    this.endAt = _endAt
    this.timedStartAt = _timedStartAt
    this.prevTaskIds = _prevTaskIds
    this.isShow = _isShow
    this.paperId = _paperId
    this.adminIds = _adminIds
    this.setting = _setting ? new TaskSetting(_setting) : _setting
    this.description = _description
    this.weight = _weight
  }
}
